

import React, {useContext} from 'react';

import {ArrowBackIos} from '@mui/icons-material';
import {
  Fade,
} from '@mui/material';
import {
  Stack,
} from '@zoom/react-ui';
import {DateTime} from 'luxon';
import {useTranslation} from 'react-i18next';
import {
  useSelector,
} from 'react-redux';
import {useNavigate} from 'react-router-dom';


import {
  getMinDate,
  VIEW_STATE,
} from './index.js';

import ApptsContext from './ApptsContext.js';
import DebugFooter from './DebugFooter.js';
import {
  EventContainer,
  LoadingContainer,
  EventStack,
  DateSection,
  SidebarContainer,
  MainContainer,
} from './Style.js';
import VotingFooter from './VotingFooter.js';
import WeekendSwitch from './WeekendSwitch.js';

import ApptInfo from 'Components/ApptInfo/index.js';
import {toDateTime} from 'Components/BookingBlocksWidget/utils.js';
import BookingDatePicker from 'Components/BookingDayPicker';
import BookingFinalizer from 'Components/BookingForm';
import DatesWindow from 'Components/DatesWindow';
import TimeFormatSwitch from 'Components/DateTimeFormatSettings/TimeFormatSwitch.js';
import MessageDisplay from 'Components/MessageDisplay';
import ErrorDisplay from 'Components/MessageDisplay/ErrorDisplay.js';
import PolicyTerms from 'Components/PolicyTerms/index.js';
import PoweredBy from 'Components/PoweredBy/index.js';
import {BreadCrumb} from 'Components/Reschedulings/Style.js';
import TimeZonePicker from 'Components/TimeZonePicker/TimeZonePicker.js';
import ZmSpinner from 'Components/ZmSpinner';
import HostContext from 'HostContext.js';
import {selectMonthAvailability} from 'Store/BookAppointmentsStore.js';
import {
  getApptEndDate,
  getDayClassName,
  getWeekAvailabilityKey,
  isAnonymousTeamAppt,
  isInApptSpanRange,
  isTeamAppt,
} from 'Utils/apptAvailabilityUtils.js';
import {EVENT_STATUS} from 'Utils/consts';
import {isCciCalendar} from 'Utils/index.js';

import 'react-datepicker/dist/react-datepicker.css';


/**
 * Appointments Page Component for Desktop Devices
 * @param {Object} props
 * @param {String} props.calendarId
 * @param {String} props.appointmentId
 * @return {ReactElement}
 */
export default function ApptsDesktop(props) {
  const {
    calendarId,
    errorInfo,
    handleSelectDate,
    handleSelectSlot,
    isBookerVoting,
    onMonthChange,
    selectedSlot,
    setCurrView,
    setTroubleShootingMode,
    troubleShootingMode,
    voterInfo,
    votes,
  } = props;
  const {
    currView,
    handleChangeTimeFormat,
    setTimeZone,
    timeFormat,
    timeZone,
  } = useContext(ApptsContext);
  const {t} = useTranslation();

  const navigate = useNavigate();
  const {isHost} = useContext(HostContext);
  const appt = useSelector((s) => s.bookAppointments.appt);
  const apptDays = useSelector((s) => s.bookAppointments.apptDays);
  const apptDaysData = useSelector((s) => selectMonthAvailability(s, timeZone));
  const loadingDays = apptDaysData?.some((week) => week.loading);
  const apptSmsEnabled = appt?.smsNotification || appt?.customNotifications?.attendeeSmsReminder?.enabled;
  const viewingMonth = useSelector((s) => s.bookAppointments.viewingMonth);

  const initialDataLoading = useSelector((s) => s.bookAppointments.loadingInitialData || !s.bookAppointments.appt);
  const loadingUserData = useSelector((s) => s.bookAppointments.loadingUserData);

  const currentTimeCutoff = useSelector((s) => s.bookAppointments.currentTimeCutoff);
  const selectedDate = useSelector((s) => s.bookAppointments.selectedDate);
  const createrDisplayName = useSelector((s) => s.bookAppointments.createrDisplayName);
  // const slotsForDt = useMemo(() => toDateTime(new Date(selectedDate), timeZone, true), [selectedDate, timeZone]);
  const status = appt?.status;
  const isCancelled = status === EVENT_STATUS.CANCELLED;
  const isExpired = getApptEndDate(appt, appt?.start?.timeZone) < DateTime.now().setZone(timeZone);

  const isCciAccount = isCciCalendar(appt?.organizer?.email);

  // Backwards compatibility: if appt.active is undefined, treat it as active
  const isInactive = (appt?.active === false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayDt = toDateTime(today, timeZone, true);

  // const addADay = (dateString) => {
  //   const result = new Date(dateString);
  //   result.setDate(result.getDate() + 1);
  //   return result;
  // };

  // const minDateWithSlots = useMemo(() => {
  //   if (!apptInstList?.list || !appt) return null;
  //   let minDate = getMinDate(appt);
  //   minDate.setHours(0, 0, 0, 0);

  //   // check if minDate has slots available
  //   let slotsForMinDate = [];
  //   if (selectedApptsList.every(({id}) => apptDetails[id]?.data && !apptDetails[id]?.loading)) {
  //     console.log('data populated, getting available times');
  //     const apptsList = selectedApptsList.map(({id}) => apptDetails[id]?.data);
  //     slotsForMinDate = getTimesForDay(appt, apptsList, minDate, timeZone);
  //   } else {
  //     console.log('waiting for missing details');
  //     return;
  //   }

  //   const endDate = new Date(getEndDate(appt));
  //   endDate.setHours(0, 0, 0, 0);
  //   if (slotsForMinDate?.length === 0) {
  //     if (minDate.getTime() === endDate.getTime()) return minDate; // no available dates
  //     minDate = addADay(minDate);
  //     // try to find the next date with available slots
  //     while (!isValidDate(minDate, appt, toDateTime(currentTimeCutoff, timeZone)) &&
  //       minDate.getTime() <= endDate.getTime()) {
  //       minDate = addADay(minDate);
  //     }
  //   }

  //   if (appt && apptInstList.hasLoaded && isValidDate(minDate, appt, toDateTime(currentTimeCutoff, timeZone)) &&
  //   ((new Date(selectedDate)).setHours(0, 0, 0, 0) < minDate.setHours(0, 0, 0, 0))) {
  //     dispatch(setSelectedDate(minDate.toISOString()));
  //   }
  //   return minDate;
  // }, [appt, apptDetails, apptInstList.hasLoaded, apptInstList?.list,
  //   currentTimeCutoff, dispatch, selectedApptsList, selectedDate, timeZone]);

  return (
    <>
      {(isHost) &&
        <BreadCrumb onClick={() => {
          navigate(-1);
        }}>
          <ArrowBackIos style={{fontSize: '10px'}} /> {t('landing.backToBookingsNav')}
        </BreadCrumb>
      }
      <EventContainer>
        {(initialDataLoading || loadingUserData || errorInfo.isError) ? (
          (errorInfo.isError) ? (
            <ErrorDisplay errorInfo={errorInfo}/>
          ) : (
            <LoadingContainer>
              <ZmSpinner size="40" absolute/>
            </LoadingContainer>
          )
        ) : (
          (isCancelled || isExpired || isInactive) ?
            (isExpired) ?
            <MessageDisplay
              appointment={appt}
              header={t('cancellation.cancelEventExpiredHeader')}
              body={t('cancellation.cancelEventExpiredMsg')}
              displayName={createrDisplayName}
              isCciAccount={isCciAccount}
              isExpired={isExpired}
            /> :
            <MessageDisplay
              header={t('error.errorMsgHeader404')}
              body={t('error.errorMsgBody404')}
              isCancelled={isCancelled || isInactive}
              isCciAccount={isCciAccount}
            /> :
          <Fade in={true}>
            <EventStack>
              {[VIEW_STATE.DATE_VIEW,
                VIEW_STATE.SLOT_VIEW].includes(currView) && <>
                <SidebarContainer>
                  <ApptInfo
                    appt={appt}
                    hideAvatar={isCciAccount || isAnonymousTeamAppt(appt)}
                    toCompletedScreen={false}
                  />
                  <DateSection>
                    <BookingDatePicker
                      displayTodayBtn={
                        toDateTime(getMinDate(appt), timeZone).startOf('day').valueOf() <= todayDt.valueOf()
                      }
                      inline
                      formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 1)}
                      onChange={handleSelectDate}
                      onMonthChange={(date) => {
                        onMonthChange(date);
                      }}
                      selected={new Date(selectedDate)}
                      loadingMonth={loadingDays && !isTeamAppt(appt)}
                      disableNextMonth={loadingDays && isTeamAppt(appt)}
                      filterDate={(date) => {
                        const weekData = apptDays?.[getWeekAvailabilityKey(toDateTime(date, timeZone), timeZone)];
                        return (
                          isInApptSpanRange(date, timeZone, appt, currentTimeCutoff) &&
                          (!isTeamAppt(appt) ||
                            !(
                              weekData?.loading &&
                              toDateTime(viewingMonth, timeZone, true).endOf('month').startOf('day')
                                .diff(toDateTime(date, timeZone, true)).milliseconds < 0
                            )
                          )
                        );
                      }}
                      minDate={getMinDate(appt)}
                      dayClassName={(date) => getDayClassName(date, selectedDate, appt,
                        toDateTime(currentTimeCutoff, timeZone), apptDays)}
                      maxDate={getApptEndDate(appt, timeZone).toJSDate()}
                    />
                  </DateSection>
                  {!isCciAccount && <PoweredBy />}
                  <PolicyTerms />
                </SidebarContainer>
              </>}
              <MainContainer>
                {[VIEW_STATE.DATE_VIEW,
                  VIEW_STATE.SLOT_VIEW].includes(currView) && <>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <TimeZonePicker sx={{marginLeft: -1}} locked={appt?.lockTimezoneEnabled}
                      value={timeZone} onChange={setTimeZone} />
                    <Stack direction="row" gap="1.5rem">
                      <TimeFormatSwitch
                        timeFormat={timeFormat}
                        handleChangeTimeFormat={handleChangeTimeFormat}
                      />
                      <WeekendSwitch />
                    </Stack>
                  </Stack>

                  <DatesWindow
                    onSelectSlot={handleSelectSlot}
                    calendarId={calendarId}
                    selectedSlot={selectedSlot}
                    apptCap={appt.capacity}
                    timeZone={timeZone}
                    isBookerVoting={isBookerVoting}
                    votes={votes}
                    setCurrView={setCurrView}
                    voterInfo={voterInfo}
                    troubleShootingMode={troubleShootingMode}
                    hasTroubleShootingMode={isHost}
                  />

                  {
                    isBookerVoting &&
                    <VotingFooter
                      isBookerVoting={isBookerVoting}
                      setCurrView={setCurrView}
                      votes={votes}
                    />
                  }
                  {
                    isHost &&
                    <DebugFooter
                      troubleShootingMode={troubleShootingMode}
                      setTroubleShootingMode={setTroubleShootingMode}
                    />
                  }
                </>}
                {[VIEW_STATE.FORM_VIEW,
                  VIEW_STATE.CONFIRM_VIEW].includes(currView) && <>
                  <BookingFinalizer
                    appointment={appt}
                    appointmentSmsEnabled={apptSmsEnabled}
                    calendarId={calendarId}
                    startDateTime={selectedSlot?.start}
                  />
                  {!isCciAccount && <PoweredBy />}
                  <PolicyTerms />
                </>}
              </MainContainer>
            </EventStack>
          </Fade>
        )}
      </EventContainer>
    </>
  );
}

